import React, { useEffect, useMemo, useState, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { loadData } from './SalesPage.duck';
import { logout } from '../../ducks/auth.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import { useHistory } from 'react-router-dom';

import {
  H2,
  H3,
  Page,
  NamedLink,
  LayoutSideNavigation,
  IconInquiry,
  IconCheckmark,
  IconClose,
  IconSpinner,
  IconSuccess,
  IconAlert,
  IconDate,
  IconLocation,
  IconProfile,
} from '../../components';
import ProfileNav from '../../components/ProfileNav/ProfileNav';
import ListingLink from '../../components/ListingLink/ListingLink';
import { formatMoney } from '../../util/currency';
import { getDisplayOrderNumber } from '../../util/orderNumber';
import { SingleDatePicker } from '../../components/DatePicker/DatePickers';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './SalesPage.module.css';

const { Money } = sdkTypes;

// Custom icon components for better visual hierarchy
const IconDownload = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconTrending = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 17L9 11L13 15L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 7V13H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconFilter = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconChevronDown = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconSortAsc = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5V19M12 5L19 12M12 5L5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconSortDesc = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 19V5M12 19L5 12M12 19L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconSort = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 9L12 5L16 9M16 15L12 19L8 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconSearch = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Multi-select dropdown component for status filter
const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder, label }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => setIsOpen(!isOpen);
  const handleClose = () => setIsOpen(false);

  const handleOptionToggle = (value) => {
    const newSelected = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newSelected);
  };

  const handleSelectAll = () => {
    onChange(options.map(opt => opt.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const selectedCount = selectedValues.length;
  const displayText = selectedCount === 0 
    ? placeholder 
    : selectedCount === 1 
      ? options.find(opt => opt.value === selectedValues[0])?.label 
      : intl.formatMessage({ id: 'SalesPage.multiSelect.selectedCount' }, { count: selectedCount });

  return (
    <div className={css.multiSelectContainer} ref={dropdownRef}>
      <label className={css.filterLabel}>{label}</label>
      <div className={css.multiSelectWrapper}>
        <button
          type="button"
          className={css.multiSelectButton}
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className={css.multiSelectButtonText}>{displayText}</span>
          <IconChevronDown className={`${css.multiSelectChevron} ${isOpen ? css.chevronOpen : ''}`} />
        </button>

        {isOpen && (
          <div className={css.multiSelectDropdown}>
            <div className={css.multiSelectHeader}>
              <input
                type="text"
                placeholder={intl.formatMessage({ id: 'SalesPage.multiSelect.searchPlaceholder' })}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={css.multiSelectSearch}
                onClick={(e) => e.stopPropagation()}
              />
              <div className={css.multiSelectActions}>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className={css.multiSelectAction}
                >
                  <FormattedMessage id="SalesPage.multiSelect.selectAll" />
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className={css.multiSelectAction}
                >
                  <FormattedMessage id="SalesPage.multiSelect.clear" />
                </button>
              </div>
            </div>
            
            <div className={css.multiSelectOptions}>
              {filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`${css.multiSelectOption} ${isSelected ? css.optionSelected : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleOptionToggle(option.value)}
                      className={css.multiSelectCheckbox}
                    />
                    <span className={css.optionLabel}>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const OrderStatusText = ({ lastTransition }) => {
  const getStatusId = (lastTransition) => {
    switch (lastTransition) {
      case 'transition/request-payment':
      case 'transition/request-payment-after-inquiry':
        return 'InboxPage.default-purchase.pending-payment.status';
      case 'transition/inquire':
        return 'InboxPage.default-purchase.inquiry.status';
      case 'transition/confirm-payment':
        return 'InboxPage.default-purchase.purchased.status';
      case 'transition/validate-order':
      case 'transition/operator-validate-order':
        return 'InboxPage.default-purchase.validated.status';
      // New process transitions
      case 'transition/mark-shipped':
      case 'transition/operator-mark-shipped':
      // Old process transitions (backward compatibility)
      case 'transition/mark-delivered':
      case 'transition/operator-mark-delivered':
        return 'InboxPage.default-purchase.shipped.status';
      // New process transitions
      case 'transition/operator-mark-received':
      // Old process transitions (backward compatibility)
      case 'transition/mark-received':
      case 'transition/mark-received-from-purchased':
        return 'InboxPage.default-purchase.received.status';
      case 'transition/operator-mark-completed':
      case 'transition/auto-complete':
        return 'InboxPage.default-purchase.completed.status';
      case 'transition/cancel':
      case 'transition/cancel-from-disputed':
        return 'InboxPage.default-purchase.canceled.status';
      case 'transition/dispute':
      case 'transition/operator-dispute':
        return 'InboxPage.default-purchase.disputed.status';
      case 'transition/expire-payment':
        return 'InboxPage.default-purchase.payment-expired.status';
      // New process transitions
      case 'transition/review-1-by-customer':
      case 'transition/expire-review-period':
      // Old process transitions (backward compatibility)
      case 'transition/review-1-by-provider':
      case 'transition/review-2-by-customer':
      case 'transition/review-2-by-provider':
      case 'transition/expire-customer-review-period':
      case 'transition/expire-provider-review-period':
        return 'InboxPage.default-purchase.reviewed.status';
      default:
        return 'InboxPage.default-purchase.inquiry.status';
    }
  };

  const statusId = getStatusId(lastTransition);
  return (
    <span className={css.statusText}>
      <FormattedMessage id={statusId} />
    </span>
  );
};

// Statistics cards component (without icon)
const StatisticsCard = ({ title, value, trend, className }) => (
  <div className={`${css.statCard} ${className || ''}`}>
    <div className={css.statContent}>
      <h4 className={css.statTitle}>{title}</h4>
      <div className={css.statValue}>{value}</div>
      {trend && (
        <div className={`${css.statTrend} ${trend > 0 ? css.trendPositive : css.trendNegative}`}>
          <IconTrending className={css.trendIcon} />
          {Math.abs(trend)}%
        </div>
      )}
    </div>
  </div>
);

// Sortable table header component
const SortableHeader = ({ field, currentSortField, sortDirection, onSort, children }) => {
  const isActive = currentSortField === field;
  
  const handleClick = () => {
    onSort(field);
  };

  return (
    <div className={css.sortableHeader} onClick={handleClick}>
      <span>{children}</span>
      <span className={css.sortIcon}>
        {isActive ? (
          sortDirection === 'asc' ? (
            <IconSortAsc className={css.sortIconActive} />
          ) : (
            <IconSortDesc className={css.sortIconActive} />
          )
        ) : (
          <IconSort className={css.sortIconInactive} />
        )}
      </span>
    </div>
  );
};

const SaleRow = ({ sale, onViewDetails }) => {
  const intl = useIntl();
  
  // Extract data from sale object - Sharetribe SDK format
  const id = sale.id;
  const attributes = sale.attributes || {};
  const lastTransition = attributes.lastTransition || sale.lastTransition;
  
  const createdAt = sale.createdAt || attributes.createdAt;
  const orderDate = createdAt ? new Date(createdAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }) : '-';
  const payout = sale.payoutTotal || attributes.payoutTotal;
  const total = payout ? formatMoney(intl, payout) : 'N/A';
  const customerName = sale.customer?.attributes?.profile?.displayName || 'Customer';
  
  // Get order number - show custom number (#NEO-12345) or transaction UUID
  const orderNumber = getDisplayOrderNumber(sale);
  
  // Check if multi-item purchase
  const isMultiItem = attributes.protectedData?.multiItemPurchase === true;
  const additionalListings = attributes.protectedData?.additionalListings || [];
  const totalItems = isMultiItem ? 1 + additionalListings.length : 1;
  
  // Display product name or item count
  const listingTitle = sale.listing?.attributes?.title || 'Unknown Product';
  const productDisplay = totalItems > 1 
    ? intl.formatMessage({ id: 'SalesPage.itemCount' }, { count: totalItems })
    : listingTitle;

  const handleClick = () => {
    if (id?.uuid && typeof onViewDetails === 'function') {
      onViewDetails(id.uuid);
    }
  };
  
  // Check if order is pending shipment (awaiting shipment)
  const isPendingShipment =
    lastTransition === 'transition/confirm-payment' ||
    lastTransition === 'transition/validate-order' ||
    lastTransition === 'transition/operator-validate-order';

  const rowClassName = isPendingShipment 
    ? `${css.tableRow} ${css.tableRowPending}` 
    : css.tableRow;

  return (
    <div className={rowClassName} onClick={handleClick} title={`Order from ${customerName} - ${orderDate}`}>
      <div className={css.cell} data-label={intl.formatMessage({ id: 'SalesPage.table.orderNumber' })}>
        <span className={css.orderNumber}>{orderNumber}</span>
      </div>
      <div className={css.cell} data-label={intl.formatMessage({ id: 'SalesPage.table.date' })}>
        <div className={css.dateWrapper}>
          <span className={css.dateText}>{orderDate}</span>
        </div>
      </div>
      <div className={css.cell} data-label={intl.formatMessage({ id: 'SalesPage.table.product' })}>
        <div className={css.productCell}>
          {sale.listing && totalItems === 1 ? (
            <ListingLink listing={sale.listing} className={css.productLink}>
              {productDisplay}
            </ListingLink>
          ) : (
            <span className={css.productText}>{productDisplay}</span>
          )}
        </div>
      </div>
      <div className={css.cell} data-label={intl.formatMessage({ id: 'SalesPage.table.customer' })}>
        <div className={css.customerCell}>
          <IconProfile className={css.customerIcon} />
          <span className={css.customerName}>{customerName}</span>
        </div>
      </div>
      <div className={css.cell} data-label={intl.formatMessage({ id: 'SalesPage.table.total' })}>
        <span className={css.totalAmount}>{total}</span>
      </div>
      <div className={css.cell} data-label={intl.formatMessage({ id: 'SalesPage.table.status' })}>
        <OrderStatusText lastTransition={lastTransition} />
      </div>
    </div>
  );
};

export const SalesPageComponent = props => {
  const intl = useIntl();
  const config = useConfiguration();
  const { currentUser, scrollingDisabled, loadSalesData, onLogout } = props;
  const history = useHistory();

  const user = ensureCurrentUser(currentUser);

  // Filters
  const [statusFilters, setStatusFilters] = useState([]);
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Search and sort
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('date'); // 'orderNumber', 'date', 'customer', 'amount', 'status'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  
  // Column filters
  const [dateFilterStart, setDateFilterStart] = useState('');
  const [dateFilterEnd, setDateFilterEnd] = useState('');
  const [customerFilter, setCustomerFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  // Load sales data on mount and when filters change
  useEffect(() => {
    loadSalesData({
      statusFilters,
      createdAtStart: dateStart ? dateStart.toISOString() : undefined,
      createdAtEnd: dateEnd ? dateEnd.toISOString() : undefined,
    });
  }, [loadSalesData, statusFilters, dateStart, dateEnd]);

  const { fetchInProgress, fetchSalesError, transactionRefs, transactions } = props;
  const sales = transactions || [];

  const title = intl.formatMessage({ id: 'SalesPage.title' });

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Extract unique customers for filter dropdown
  const uniqueCustomers = useMemo(() => {
    const customers = new Set();
    sales.forEach(sale => {
      const customerName = sale.customer?.attributes?.profile?.displayName;
      if (customerName) {
        customers.add(customerName);
      }
    });
    return Array.from(customers).sort().map(name => ({ value: name, label: name }));
  }, [sales]);

  // Get status label from transition
  const getStatusLabel = (transition) => {
    if (!transition) return '';
    try {
      const statusId = OrderStatusText({ lastTransition: transition }).props.children.props.id;
      return intl.formatMessage({ id: statusId });
    } catch (error) {
      return transition;
    }
  };

  const uniqueStatuses = useMemo(() => {
    // Use Map to store transition -> label mapping
    const statusMap = new Map();
    sales.forEach(sale => {
      const status = sale.attributes?.lastTransition || sale.lastTransition;
      if (status && !statusMap.has(status)) {
        const label = getStatusLabel(status);
        statusMap.set(status, label);
      }
    });
    // Convert to array of {value, label} objects and sort by label
    return Array.from(statusMap.entries())
      .map(([transition, label]) => ({ value: transition, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sales]);

  // Filter and sort sales
  const filteredAndSortedSales = useMemo(() => {
    let result = [...sales];

    // Apply search filter
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase().trim();
      result = result.filter(sale => {
        const orderNumber = getDisplayOrderNumber(sale).toLowerCase();
        const customerName = (sale.customer?.attributes?.profile?.displayName || '').toLowerCase();
        return orderNumber.includes(lowerSearchTerm) || customerName.includes(lowerSearchTerm);
      });
    }

    // Apply date range filter
    if (dateFilterStart || dateFilterEnd) {
      result = result.filter(sale => {
        const saleDate = new Date(sale.createdAt || sale.attributes?.createdAt);
        saleDate.setHours(0, 0, 0, 0); // Reset time to compare only dates
        
        let matchesStart = true;
        let matchesEnd = true;
        
        if (dateFilterStart) {
          const startDate = new Date(dateFilterStart);
          startDate.setHours(0, 0, 0, 0);
          matchesStart = saleDate >= startDate;
        }
        
        if (dateFilterEnd) {
          const endDate = new Date(dateFilterEnd);
          endDate.setHours(23, 59, 59, 999);
          matchesEnd = saleDate <= endDate;
        }
        
        return matchesStart && matchesEnd;
      });
    }

    // Apply customer filter (multi-select)
    if (customerFilter.length > 0) {
      result = result.filter(sale => {
        const customerName = sale.customer?.attributes?.profile?.displayName || '';
        return customerFilter.includes(customerName);
      });
    }

    // Apply status filter (multi-select)
    if (statusFilter.length > 0) {
      result = result.filter(sale => {
        const status = sale.attributes?.lastTransition || sale.lastTransition;
        return statusFilter.includes(status);
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let compareA, compareB;

      switch (sortField) {
        case 'orderNumber':
          compareA = getDisplayOrderNumber(a).toLowerCase();
          compareB = getDisplayOrderNumber(b).toLowerCase();
          break;
        case 'date':
          compareA = new Date(a.createdAt || a.attributes?.createdAt);
          compareB = new Date(b.createdAt || b.attributes?.createdAt);
          break;
        case 'customer':
          compareA = (a.customer?.attributes?.profile?.displayName || '').toLowerCase();
          compareB = (b.customer?.attributes?.profile?.displayName || '').toLowerCase();
          break;
        case 'amount':
          const payoutA = a.payoutTotal || a.attributes?.payoutTotal;
          const payoutB = b.payoutTotal || b.attributes?.payoutTotal;
          compareA = payoutA?.amount || 0;
          compareB = payoutB?.amount || 0;
          break;
        case 'status':
          compareA = (a.attributes?.lastTransition || a.lastTransition || '').toLowerCase();
          compareB = (b.attributes?.lastTransition || b.lastTransition || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (compareA < compareB) return sortDirection === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [sales, searchTerm, dateFilterStart, dateFilterEnd, customerFilter, statusFilter, sortField, sortDirection]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalSales = sales.length;
    const totalRevenue = sales.reduce((sum, sale) => {
      const payout = sale.payoutTotal || sale.attributes?.payoutTotal;
      return sum + (payout?.amount || 0);
    }, 0);
    
    // Count completed sales (received state and beyond)
    const completedTransitions = [
      'transition/operator-mark-received',
      'transition/mark-received',
      'transition/mark-received-from-purchased',
      'transition/operator-mark-completed',
      'transition/auto-complete',
      'transition/review-1-by-customer',
      'transition/review-1-by-provider',
      'transition/review-2-by-customer',
      'transition/review-2-by-provider',
      'transition/expire-review-period',
    ];
    
    const completedSales = sales.filter(sale => {
      const transition = sale.attributes?.lastTransition || sale.lastTransition;
      return completedTransitions.includes(transition);
    }).length;
    
    // Count pending sales (need action from provider)
    const pendingSales = sales.filter(sale => {
      const transition = sale.attributes?.lastTransition || sale.lastTransition;
      return (
        transition === 'transition/confirm-payment' ||
        transition === 'transition/validate-order' ||
        transition === 'transition/operator-validate-order'
      );
    }).length;

    return {
      totalSales,
      totalRevenue,
      completedSales,
      pendingSales,
    };
  }, [sales]);

  const csvData = useMemo(() => {
    return sales.map(sale => ({
      id: sale.id?.uuid,
      date: sale.createdAt || sale.attributes?.createdAt,
      listing: sale.listing?.attributes?.title,
      customer: sale.customer?.attributes?.profile?.displayName,
      payin: sale.payinTotal?.amount,
      payout: sale.payoutTotal?.amount,
      currency: sale.payoutTotal?.currency,
      status: sale.lastTransition,
    }));
  }, [sales]);

  const downloadCsv = () => {
    const params = new URLSearchParams();
    statusFilters.forEach(s => params.append('status', s));
    if (dateStart) params.set('start', dateStart.toISOString());
    if (dateEnd) params.set('end', dateEnd.toISOString());
    const url = `/api/sales-export?${params.toString()}`;
    window.location.assign(url);
  };

  const handleViewDetails = saleId => {
    const id = saleId?.uuid || saleId;
    if (id) history.push(`/sale/${id}`);
  };

  const clearFilters = () => {
    setStatusFilters([]);
    setDateStart(null);
    setDateEnd(null);
  };

  const clearColumnFilters = () => {
    setSearchTerm('');
    setDateFilterStart('');
    setDateFilterEnd('');
    setCustomerFilter([]);
    setStatusFilter([]);
  };

  const hasActiveFilters = statusFilters.length > 0 || dateStart !== null || dateEnd !== null;
  const hasActiveColumnFilters = searchTerm || dateFilterStart || dateFilterEnd || customerFilter.length > 0 || statusFilter.length > 0;

  const activeFilterCount = [
    dateFilterStart,
    dateFilterEnd,
    customerFilter.length > 0,
    statusFilter.length > 0,
  ].filter(Boolean).length;

  return (
    <Page className={css.root} title={title} robots="noindex, nofollow" scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation
        topbar={<TopbarContainer />}
        sideNav={
          <ProfileNav
            currentPage="SalesPage"
            currentUser={currentUser}
            className={css.nav}
            onLogout={onLogout}
          />
        }
        sideNavDismissable
        sideNavToggleAriaLabels={{
          show: intl.formatMessage({ id: 'LayoutSideNavigation.showNav' }),
          hide: intl.formatMessage({ id: 'LayoutSideNavigation.hideNav' }),
        }}
        sideNavToggleVisibleLabel={intl.formatMessage({ id: 'LayoutSideNavigation.menuLabel' })}
        footer={<FooterContainer />}
      >
        <div className={css.content}>

          <div className={css.header}>
            <H2 as="h1" className={css.pageTitle}>
              <FormattedMessage id="SalesPage.heading" />
            </H2>
            <p className={css.pageDescription}>
              <FormattedMessage id="SalesPage.description" />
            </p>
          </div>

          {/* Statistics Cards */}
          {sales.length > 0 && (
            <div className={css.statsGrid}>
              <StatisticsCard
                title={intl.formatMessage({ id: 'SalesPage.stats.totalSales' })}
                value={stats.totalSales}
                className={css.statCardSales}
              />
              <StatisticsCard
                title={intl.formatMessage({ id: 'SalesPage.stats.totalRevenue' })}
                value={formatMoney(intl, new Money(stats.totalRevenue, 'EUR'))}
                className={css.statCardRevenue}
              />
              <StatisticsCard
                title={intl.formatMessage({ id: 'SalesPage.stats.completedOrders' })}
                value={stats.completedSales}
                className={css.statCardCompleted}
              />
              <StatisticsCard
                title={intl.formatMessage({ id: 'SalesPage.stats.pendingOrders' })}
                value={stats.pendingSales}
                className={css.statCardPending}
              />
            </div>
          )}

          {/* Simple Export Button */}
          {/* Bouton export CSV caché
          {sales.length > 0 && (
            <div className={css.exportContainer}>
              <button 
                className={css.exportBtnSimple} 
                onClick={downloadCsv} 
                disabled={sales.length === 0}
                title={intl.formatMessage({ id: 'SalesPage.exportTooltip' })}
              >
                <IconDownload className={css.exportIcon} />
                <FormattedMessage id="SalesPage.exportCsv" />
              </button>
            </div>
          )}
          */}

          {fetchInProgress ? (
            <div className={css.loadingContainer}>
              <IconSpinner className={css.loadingSpinner} />
              <p className={css.loadingText}>
                <FormattedMessage id="SalesPage.loading" />
              </p>
            </div>
          ) : fetchSalesError ? (
            <div className={css.errorContainer}>
              <IconAlert className={css.errorIcon} />
              <H3 as="h3" className={css.errorTitle}>
                <FormattedMessage id="SalesPage.errorTitle" />
              </H3>
              <p className={css.errorDescription}>
                {fetchSalesError.message}
              </p>
            </div>
          ) : sales.length > 0 ? (
            <>
              {/* Search and Filters Bar */}
              <div className={css.searchAndFiltersContainer}>
                <div className={css.searchRow}>
                  <div className={css.searchWrapper}>
                    <IconSearch className={css.searchIcon} />
                    <input
                      type="text"
                      placeholder={intl.formatMessage({ id: 'SalesPage.searchPlaceholder' })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={css.searchInput}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className={css.clearSearchBtn}
                        title={intl.formatMessage({ id: 'SalesPage.clearSearch' })}
                      >
                        <IconClose className={css.clearSearchIcon} />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className={[
                      css.filterToggleBtn,
                      showFilters ? css.filterToggleBtnOpen : '',
                      activeFilterCount > 0 ? css.filterToggleBtnActive : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <IconFilter className={css.filterToggleBtnIcon} />
                    <span className={css.filterToggleBtnText}>
                      <FormattedMessage id="SalesPage.filter.search" defaultMessage="Filtres" />
                    </span>
                    {activeFilterCount > 0 && (
                      <span className={css.filterBadgeCount}>{activeFilterCount}</span>
                    )}
                  </button>
                </div>

                {showFilters && (
                  <div className={css.filtersPanel}>
                    <div className={css.filtersGrid}>
                      <div className={css.filterItem}>
                        <label className={css.filterLabel}>
                          <FormattedMessage id="SalesPage.filter.dateStart" />
                        </label>
                        <input
                          type="date"
                          value={dateFilterStart}
                          onChange={(e) => setDateFilterStart(e.target.value)}
                          onClick={(e) => {
                            try { e.target.showPicker(); } catch (err) { /* unsupported */ }
                          }}
                          className={css.datePickerInput}
                        />
                      </div>
                      <div className={css.filterItem}>
                        <label className={css.filterLabel}>
                          <FormattedMessage id="SalesPage.filter.dateEnd" />
                        </label>
                        <input
                          type="date"
                          value={dateFilterEnd}
                          onChange={(e) => setDateFilterEnd(e.target.value)}
                          onClick={(e) => {
                            try { e.target.showPicker(); } catch (err) { /* unsupported */ }
                          }}
                          className={css.datePickerInput}
                        />
                      </div>
                      <div className={css.filterItem}>
                        <MultiSelectDropdown
                          options={uniqueCustomers}
                          selectedValues={customerFilter}
                          onChange={setCustomerFilter}
                          placeholder={intl.formatMessage({ id: 'SalesPage.filter.selectCustomers' })}
                          label={intl.formatMessage({ id: 'SalesPage.filter.customer' })}
                        />
                      </div>
                      <div className={css.filterItem}>
                        <MultiSelectDropdown
                          options={uniqueStatuses}
                          selectedValues={statusFilter}
                          onChange={setStatusFilter}
                          placeholder={intl.formatMessage({ id: 'SalesPage.filter.selectStatuses' })}
                          label={intl.formatMessage({ id: 'SalesPage.filter.status' })}
                        />
                      </div>
                    </div>
                    {hasActiveColumnFilters && (
                      <div className={css.filtersPanelFooter}>
                        <button
                          onClick={clearColumnFilters}
                          className={css.clearFiltersBtn}
                          title={intl.formatMessage({ id: 'SalesPage.clearAllFilters' })}
                        >
                          <FormattedMessage id="SalesPage.clearAllFilters" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className={css.searchInfo}>
                  <FormattedMessage
                    id="SalesPage.searchResultsCount"
                    values={{ count: filteredAndSortedSales.length, total: sales.length }}
                  />
                </div>
              </div>

              <div className={css.table}>
                <div className={css.tableHeader}>
                  <div className={css.cell}>
                    <SortableHeader 
                      field="orderNumber" 
                      currentSortField={sortField} 
                      sortDirection={sortDirection} 
                      onSort={handleSort}
                    >
                      <FormattedMessage id="SalesPage.table.orderNumber" />
                    </SortableHeader>
                  </div>
                  <div className={css.cell}>
                    <SortableHeader 
                      field="date" 
                      currentSortField={sortField} 
                      sortDirection={sortDirection} 
                      onSort={handleSort}
                    >
                      <FormattedMessage id="SalesPage.table.date" />
                    </SortableHeader>
                  </div>
                  <div className={css.cell}><FormattedMessage id="SalesPage.table.product" /></div>
                  <div className={css.cell}>
                    <SortableHeader 
                      field="customer" 
                      currentSortField={sortField} 
                      sortDirection={sortDirection} 
                      onSort={handleSort}
                    >
                      <FormattedMessage id="SalesPage.table.customer" />
                    </SortableHeader>
                  </div>
                  <div className={css.cell}>
                    <SortableHeader 
                      field="amount" 
                      currentSortField={sortField} 
                      sortDirection={sortDirection} 
                      onSort={handleSort}
                    >
                      <FormattedMessage id="SalesPage.table.total" />
                    </SortableHeader>
                  </div>
                  <div className={css.cell}>
                    <SortableHeader 
                      field="status" 
                      currentSortField={sortField} 
                      sortDirection={sortDirection} 
                      onSort={handleSort}
                    >
                      <FormattedMessage id="SalesPage.table.status" />
                    </SortableHeader>
                  </div>
                </div>
                {filteredAndSortedSales.map(sale => (
                  <SaleRow key={sale.id.uuid} sale={sale} onViewDetails={handleViewDetails} />
                ))}
              </div>
            </>
          ) : (
            <div className={css.emptyState}>
              <IconInquiry className={css.emptyStateIcon} />
              <H3 as="h3" className={css.emptyStateTitle}>
                <FormattedMessage id="SalesPage.noSalesTitle" />
              </H3>
              <p className={css.emptyStateDescription}>
                <FormattedMessage id="SalesPage.noSalesDescription" />
              </p>
              <NamedLink className={css.browseButton} name="ManageListingsPage">
                <FormattedMessage id="SalesPage.manageListings" />
              </NamedLink>
            </div>
          )}
        </div>
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const salesPageState = state.SalesPage || {};
  const { fetchInProgress = false, fetchSalesError = null, transactionRefs = [] } = salesPageState;

  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    fetchInProgress,
    fetchSalesError,
    transactionRefs,
    transactions: getMarketplaceEntities(state, transactionRefs),
  };
};

const mapDispatchToProps = dispatch => ({
  loadSalesData: params => dispatch(loadData(params)),
  onLogout: () => dispatch(logout()),
});

const SalesPage = compose(connect(mapStateToProps, mapDispatchToProps))(SalesPageComponent);

// Enable SSR
SalesPage.loadData = loadData;

export default SalesPage;


